import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import Layout from "@/project/propertyPortal/components/Layout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/project/propertyPortal/components/ui/card";
import { Home, CheckCircle, Clock, AlertCircle } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import { formatDate } from "../../utils/dateFormatter";

const Dashboard = () => {
  const navigate = useNavigate();
  const propertyMasterSchemaName = "Property Master";
  const rehabEstimateSchemaName = "Rehab Estimate";
  const comparisonPropertySchemaName = "Compare Property"; // Try "Compare Property" first
  const myPropertySchemaName = "My Property";

  // Fetch Property Master data
  const { data: propertyMasterColumnData } = useQuery({
    queryKey: ["columnGridData", propertyMasterSchemaName],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(
          propertyMasterSchemaName
        );
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!propertyMasterSchemaName,
    retry: false,
  });

  const { data: propertyMasterData } = useQuery({
    queryKey: [
      "property-master-all",
      propertyMasterSchemaName,
      propertyMasterColumnData,
    ],
    queryFn: async () => {
      if (!propertyMasterColumnData?.grid) {
        return { results: [], count: 0 };
      }
      const allColumns = propertyMasterColumnData.grid.map(
        (field: any) => field.field_name
      );
      if (!allColumns.map((col: string) => col.toLowerCase()).includes("id")) {
        allColumns.push("id");
      }
      if (
        !allColumns.map((col: string) => col.toLowerCase()).includes("date")
      ) {
        allColumns.push("date");
      }
      try {
        const response = await DynamicModuleService.getGridCoByConfig(
          propertyMasterSchemaName,
          1,
          10000,
          allColumns,
          undefined
        );
        // Handle case where response is undefined (e.g., 401 error)
        if (response === undefined || response === null) {
          console.warn(
            "Property Master API returned undefined, returning default structure"
          );
          return { results: [], count: 0 };
        }
        console.log("Property Master response:", response);
        return response ?? { results: [], count: 0 };
      } catch (error: any) {
        console.error("Error fetching property master:", error);
        console.error(
          "Error details:",
          error?.response?.data ?? error?.message
        );
        return { results: [], count: 0 };
      }
    },
    enabled: !!propertyMasterSchemaName && !!propertyMasterColumnData,
    retry: false,
  });

  // Fetch My Property data
  const { data: myPropertyColumnData } = useQuery({
    queryKey: ["columnGridData", myPropertySchemaName],
    queryFn: async () => {
      try {
        const result =
          await SchemaService.getColumnGridData(myPropertySchemaName);
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!myPropertySchemaName,
    retry: false,
  });

  const { data: myPropertyData } = useQuery({
    queryKey: ["my-property-all", myPropertySchemaName, myPropertyColumnData],
    queryFn: async () => {
      if (!myPropertyColumnData?.grid) {
        return { results: [], count: 0 };
      }
      // Only use columns that exist in the schema to avoid "Column not found" errors
      // Get all field names from the schema - these are guaranteed to exist
      const allColumns = myPropertyColumnData.grid
        .map((field: any) => field.field_name)
        .filter(Boolean); // Filter out any null/undefined values

      // Don't add any columns that don't exist in the schema
      // The API will fail if we try to fetch columns that don't exist
      try {
        const response = await DynamicModuleService.getGridCoByConfig(
          myPropertySchemaName,
          1,
          10000,
          allColumns,
          undefined
        );
        // Handle case where response is undefined (e.g., 401 error)
        if (response === undefined || response === null) {
          console.warn(
            "My Property API returned undefined, returning default structure"
          );
          return { results: [], count: 0 };
        }
        console.log("My Property response:", response);
        return response ?? { results: [], count: 0 };
      } catch (error: any) {
        console.error("Error fetching my property:", error);
        console.error(
          "Error details:",
          error?.response?.data ?? error?.message
        );
        return { results: [], count: 0 };
      }
    },
    enabled: !!myPropertySchemaName && !!myPropertyColumnData,
    retry: false,
  });

  // Fetch Rehab Estimate data
  const { data: rehabEstimateColumnData } = useQuery({
    queryKey: ["columnGridData", rehabEstimateSchemaName],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(
          rehabEstimateSchemaName
        );
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!rehabEstimateSchemaName,
    retry: false,
  });

  const { data: rehabEstimateData } = useQuery({
    queryKey: [
      "rehab-estimate-all",
      rehabEstimateSchemaName,
      rehabEstimateColumnData,
    ],
    queryFn: async () => {
      console.log(
        "Rehab Estimate query running, columnData:",
        rehabEstimateColumnData
      );
      if (!rehabEstimateColumnData?.grid) {
        console.warn(
          "Rehab Estimate column data not available, returning empty array"
        );
        return [];
      }
      const allColumns = rehabEstimateColumnData.grid.map(
        (field: any) => field.field_name
      );
      if (!allColumns.map((col: string) => col.toLowerCase()).includes("id")) {
        allColumns.push("id");
      }
      if (
        !allColumns
          .map((col: string) => col.toLowerCase())
          .includes("propertymaster_id")
      ) {
        allColumns.push("propertymaster_id");
      }
      // Note: status is not a field in the schema - it's calculated based on whether property has rehab estimate
      try {
        const response = await DynamicModuleService.getGridCoByConfig(
          rehabEstimateSchemaName,
          1,
          10000,
          allColumns,
          undefined
        );
        console.log("Rehab Estimate response:", response);
        // Handle case where response is undefined, null, or an array
        if (response === undefined || response === null) {
          console.warn(
            "Rehab Estimate response is undefined or null, returning empty array"
          );
          return [];
        }
        // If response is already an array, return it directly
        if (Array.isArray(response)) {
          return response;
        }
        // Otherwise, try to extract results or data, or return empty array
        return response?.results ?? response?.data ?? [];
      } catch (error: any) {
        console.error("Error fetching rehab estimate:", error);
        console.error(
          "Error details:",
          error?.response?.data ?? error?.message
        );
        return [];
      }
    },
    enabled: !!rehabEstimateSchemaName && !!rehabEstimateColumnData,
    retry: false,
  });

  // Fetch Comparison Property data
  const { data: comparisonPropertyColumnData } = useQuery({
    queryKey: ["columnGridData", comparisonPropertySchemaName],
    queryFn: () =>
      SchemaService.getColumnGridData(comparisonPropertySchemaName),
    enabled: !!comparisonPropertySchemaName,
    retry: false,
  });

  const { data: comparisonPropertyData } = useQuery({
    queryKey: [
      "comparison-property-all",
      comparisonPropertySchemaName,
      comparisonPropertyColumnData,
    ],
    queryFn: async () => {
      if (!comparisonPropertyColumnData?.grid) {
        return { results: [], count: 0 };
      }
      const allColumns = comparisonPropertyColumnData.grid.map(
        (field: any) => field.field_name
      );
      if (!allColumns.map((col: string) => col.toLowerCase()).includes("id")) {
        allColumns.push("id");
      }
      if (
        !allColumns
          .map((col: string) => col.toLowerCase())
          .includes("propertymaster_id")
      ) {
        allColumns.push("propertymaster_id");
      }
      try {
        const response = await DynamicModuleService.getGridCoByConfig(
          comparisonPropertySchemaName,
          1,
          10000,
          allColumns,
          undefined
        );
        // Handle case where response is undefined (e.g., 401 error)
        if (response === undefined || response === null) {
          console.warn(
            "Comparison Property API returned undefined, returning default structure"
          );
          return { results: [], count: 0 };
        }
        console.log("Comparison Property response:", response);
        return response ?? { results: [], count: 0 };
      } catch (error: any) {
        console.error("Error fetching comparison property:", error);
        console.error(
          "Error details:",
          error?.response?.data ?? error?.message
        );
        // If "Compare Property" fails, try "Comparison Property Master"
        if (comparisonPropertySchemaName === "Compare Property") {
          try {
            const altResponse = await DynamicModuleService.getGridCoByConfig(
              "Comparison Property Master",
              1,
              10000,
              allColumns,
              undefined
            );
            // Handle case where altResponse is undefined (e.g., 401 error)
            if (altResponse === undefined || altResponse === null) {
              return { results: [], count: 0 };
            }
            console.log(
              "Comparison Property Master (alt) response:",
              altResponse
            );
            return altResponse ?? { results: [], count: 0 };
          } catch (altError) {
            console.error(
              "Error fetching Comparison Property Master:",
              altError
            );
          }
        }
        return { results: [], count: 0 };
      }
    },
    enabled: !!comparisonPropertySchemaName && !!comparisonPropertyColumnData,
    retry: false,
  });

  // Calculate stats
  const stats = useMemo(() => {
    // Handle case where API returns array directly or object with results/data
    const properties = Array.isArray(propertyMasterData)
      ? propertyMasterData
      : (propertyMasterData?.results ?? propertyMasterData?.data ?? []);
    const myProperties = Array.isArray(myPropertyData)
      ? myPropertyData
      : (myPropertyData?.results ?? myPropertyData?.data ?? []);
    const rehabEstimates = !rehabEstimateData
      ? []
      : Array.isArray(rehabEstimateData)
        ? rehabEstimateData
        : (rehabEstimateData?.results ?? rehabEstimateData?.data ?? []);
    const comparisonProperties = Array.isArray(comparisonPropertyData)
      ? comparisonPropertyData
      : (comparisonPropertyData?.results ?? comparisonPropertyData?.data ?? []);

    // Get unique property IDs that have rehab estimates
    // Similar to PP_RehabEstimator - normalize keys for case-insensitive lookup
    const propertiesWithRehabEstimate = new Set<string>();
    rehabEstimates.forEach((rehab: any) => {
      // Normalize keys to lowercase for case-insensitive lookup
      const normalizedRehab: any = {};
      Object.keys(rehab).forEach(key => {
        normalizedRehab[key.toLowerCase()] = rehab[key];
      });

      // Try multiple field name variations for propertymaster_id
      const propertyId =
        normalizedRehab.propertymaster_id ??
        normalizedRehab.propertymasterid ??
        normalizedRehab.property_master_id ??
        rehab.propertymaster_id ??
        rehab.propertymaster_Id ??
        rehab.PropertyMasterId ??
        rehab.propertymasterId ??
        rehab.property_master_id ??
        rehab.propertyMasterId ??
        rehab.propertyMaster_ID;

      if (propertyId) {
        propertiesWithRehabEstimate.add(String(propertyId));
      }
    });

    // Get unique property IDs that have comparison properties
    const propertiesWithComp = new Set(
      comparisonProperties
        .map(
          (cp: any) =>
            cp.propertymaster_id ?? cp.propertymaster_Id ?? cp.PropertyMasterId
        )
        .filter(Boolean)
    );

    // Helper function to check if file upload field has data (same logic as PP_MyProperty)
    const hasFileData = (record: any, fieldName: string): boolean => {
      // Normalize keys to lowercase
      const normalized: any = {};
      Object.keys(record).forEach(key => {
        normalized[key.toLowerCase()] = record[key];
      });

      const value =
        normalized[fieldName.toLowerCase()] ??
        record[fieldName] ??
        record[fieldName.toUpperCase()];

      if (!value) {
        return false;
      }

      // Handle string (could be JSON)
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return (
              parsed.length > 0 &&
              parsed.some(
                (item: any) =>
                  item !== null && item !== undefined && item !== ""
              )
            );
          }
          return parsed !== null && parsed !== undefined && parsed !== "";
        } catch {
          return value.trim() !== "";
        }
      }

      // Handle arrays
      if (Array.isArray(value)) {
        return (
          value.length > 0 &&
          value.some(
            (item: any) => item !== null && item !== undefined && item !== ""
          )
        );
      }

      // Handle objects
      if (typeof value === "object" && value !== null) {
        return true;
      }

      return false;
    };

    // Count completed My Properties (those with file uploads)
    // Find the actual fileupload field name from the data (check first record if available)
    let fileUploadFieldName: string | null = null;
    if (myProperties.length > 0) {
      const firstRecord = myProperties[0];
      const recordKeys = Object.keys(firstRecord);
      fileUploadFieldName =
        recordKeys.find(
          key =>
            key.toLowerCase().includes("file") &&
            key.toLowerCase().includes("upload")
        ) ?? null;
    }

    // Count completed properties - if fileupload field exists, check it; otherwise count all
    const myPropertiesCompletedCount = fileUploadFieldName
      ? myProperties.filter((mp: any) => {
          return hasFileData(mp, fileUploadFieldName!);
        }).length
      : myProperties.length; // If no fileupload field, count all as completed

    // Count total properties from Property Master schema
    // Handle case where API returns array directly or object with results/data
    let totalProperties = 0;
    if (propertyMasterData) {
      // If propertyMasterData is directly an array, use its length
      if (Array.isArray(propertyMasterData)) {
        totalProperties = propertyMasterData.length;
      } else {
        // Check for count in various possible locations
        const countValue =
          propertyMasterData.count ??
          propertyMasterData.data?.count ??
          propertyMasterData.total ??
          propertyMasterData.data?.total;

        // Use count if available and valid, otherwise use array length
        if (
          countValue !== undefined &&
          countValue !== null &&
          !isNaN(Number(countValue))
        ) {
          totalProperties = Number(countValue);
        } else if (Array.isArray(properties) && properties.length > 0) {
          totalProperties = properties.length;
        }
      }
    }

    // Debug logging
    console.log("Total Properties Calculation:", {
      propertyMasterData,
      isArray: Array.isArray(propertyMasterData),
      propertiesLength: Array.isArray(properties) ? properties.length : 0,
      count:
        propertyMasterData && !Array.isArray(propertyMasterData)
          ? propertyMasterData?.count
          : undefined,
      dataCount:
        propertyMasterData && !Array.isArray(propertyMasterData)
          ? propertyMasterData?.data?.count
          : undefined,
      totalProperties,
      propertiesArray: properties,
    });

    // Count properties with pending rehab status
    // Status is "Pending" if property doesn't have a rehab estimate (same logic as PP_RehabEstimator)
    // Get all property IDs from Property Master
    const allPropertyIds = new Set<string>();
    properties.forEach((property: any) => {
      const propertyId =
        property.id ?? property.ID ?? property.Id ?? property._id;
      if (propertyId) {
        allPropertyIds.add(String(propertyId));
      }
    });

    // Count properties that don't have rehab estimates = Pending
    const pendingRehabStatusCount = Array.from(allPropertyIds).filter(
      propertyId => !propertiesWithRehabEstimate.has(propertyId)
    ).length;

    const myPropertiesCount = myPropertiesCompletedCount;
    const pendingComp = totalProperties - propertiesWithComp.size;

    // Debug logging for pending rehab status
    console.log("Pending Rehab Status Calculation:", {
      totalProperties,
      allPropertyIdsCount: allPropertyIds.size,
      propertiesWithRehabEstimateCount: propertiesWithRehabEstimate.size,
      pendingRehabStatusCount,
      rehabEstimatesCount: rehabEstimates.length,
      samplePropertyIds: Array.from(allPropertyIds).slice(0, 3),
      sampleRehabEstimateIds: Array.from(propertiesWithRehabEstimate).slice(
        0,
        3
      ),
    });

    return [
      {
        title: "Total Properties",
        value: String(totalProperties ?? 0).padStart(2, "0"),
        icon: Home,
        color: "bg-primary",
        route: "/property-master",
      },
      {
        title: "Pending Rehab (RE)",
        value: String(pendingRehabStatusCount ?? 0).padStart(2, "0"),
        icon: Clock,
        color: "bg-warning",
        route: "/rehab-estimator",
      },
      {
        title: "Pending Comp",
        value: String(pendingComp).padStart(2, "0"),
        icon: AlertCircle,
        color: "bg-info",
        route: "/comparison-property-master",
      },
      {
        title: "My Properties (Completed)",
        value: String(myPropertiesCount ?? 0).padStart(2, "0"),
        icon: CheckCircle,
        color: "bg-success",
        route: "/my-property",
      },
    ];
  }, [
    propertyMasterData,
    myPropertyData,
    rehabEstimateData,
    comparisonPropertyData,
  ]);

  // Calculate quick metrics
  const quickMetrics = useMemo(() => {
    const properties = Array.isArray(propertyMasterData)
      ? propertyMasterData
      : (propertyMasterData?.results ?? propertyMasterData?.data ?? []);
    const rehabEstimates = !rehabEstimateData
      ? []
      : Array.isArray(rehabEstimateData)
        ? rehabEstimateData
        : (rehabEstimateData?.results ?? rehabEstimateData?.data ?? []);
    const comparisonProperties = Array.isArray(comparisonPropertyData)
      ? comparisonPropertyData
      : (comparisonPropertyData?.results ?? comparisonPropertyData?.data ?? []);

    // Get unique property IDs that have comparison properties
    const propertiesWithComp = new Set(
      comparisonProperties
        .map(
          (cp: any) =>
            cp.propertymaster_id ?? cp.propertymaster_Id ?? cp.PropertyMasterId
        )
        .filter(Boolean)
    );

    // Get unique property IDs that have rehab estimates
    const propertiesWithRehab = new Set(
      rehabEstimates
        .map(
          (re: any) =>
            re.propertymaster_id ?? re.propertymaster_Id ?? re.PropertyMasterId
        )
        .filter(Boolean)
    );

    const propertiesWithComparable = propertiesWithComp.size;
    const rehabEstimateNotCompleted =
      properties.length - propertiesWithRehab.size;
    const compAnalysisPending = properties.length - propertiesWithComp.size;

    return [
      { label: "Properties with comparable", count: propertiesWithComparable },
      {
        label: "Rehab estimate not completed",
        count: rehabEstimateNotCompleted,
      },
      { label: "Comp analysis pending", count: compAnalysisPending },
    ];
  }, [propertyMasterData, rehabEstimateData, comparisonPropertyData]);

  // Generate chart data from actual property dates
  const chartData = useMemo(() => {
    const properties = Array.isArray(propertyMasterData)
      ? propertyMasterData
      : (propertyMasterData?.results ?? propertyMasterData?.data ?? []);

    // Group properties by month
    const monthMap = new Map<string, number>();

    properties.forEach((property: any) => {
      const dateStr =
        property.date ??
        property.Date ??
        property.created_at ??
        property.createdAt;
      if (dateStr) {
        try {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            const monthKey = date.toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            });
            monthMap.set(monthKey, (monthMap.get(monthKey) ?? 0) + 1);
          }
        } catch {
          // Invalid date, skip
        }
      }
    });

    // Convert to array and sort by date
    const chartDataArray = Array.from(monthMap.entries())
      .map(([month, count]) => ({ month, properties: count }))
      .sort((a, b) => {
        const dateA = new Date(a.month);
        const dateB = new Date(b.month);
        return dateA.getTime() - dateB.getTime();
      });

    // If no data, return empty array
    return chartDataArray.length > 0
      ? chartDataArray
      : [{ month: "No Data", properties: 0 }];
  }, [propertyMasterData]);

  // Get current date for metrics header
  const currentDate = formatDate(new Date());

  // Debug: Log data status
  console.log("Dashboard Data Status:", {
    propertyMaster: {
      hasColumnData: !!propertyMasterColumnData,
      hasData: !!propertyMasterData,
      count: Array.isArray(propertyMasterData)
        ? propertyMasterData.length
        : (propertyMasterData?.results?.length ??
          propertyMasterData?.data?.length ??
          0),
    },
    myProperty: {
      hasColumnData: !!myPropertyColumnData,
      hasData: !!myPropertyData,
      count: Array.isArray(myPropertyData)
        ? myPropertyData.length
        : (myPropertyData?.results?.length ??
          myPropertyData?.data?.length ??
          0),
    },
    rehabEstimate: {
      hasColumnData: !!rehabEstimateColumnData,
      hasData: !!rehabEstimateData,
      count: Array.isArray(rehabEstimateData)
        ? rehabEstimateData.length
        : (rehabEstimateData?.results?.length ??
          rehabEstimateData?.data?.length ??
          0),
    },
    comparisonProperty: {
      hasColumnData: !!comparisonPropertyColumnData,
      hasData: !!comparisonPropertyData,
      count: Array.isArray(comparisonPropertyData)
        ? comparisonPropertyData.length
        : (comparisonPropertyData?.results?.length ??
          comparisonPropertyData?.data?.length ??
          0),
    },
  });

  return (
    <Layout>
      <div className="space-y-4 lg:space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground lg:text-3xl">
            Dashboard
          </h1>
          <p className="mt-1 text-muted-foreground">
            Property Investment Overview
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {stats.map(stat => {
            const Icon = stat.icon;
            return (
              <Card
                key={stat.title}
                className="cursor-pointer border-none shadow-lg transition-shadow hover:shadow-xl"
                onClick={() => {
                  if (stat.route) {
                    navigate(stat.route);
                  }
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {stat.title}
                      </p>
                      <p className="mt-2 text-2xl font-bold text-foreground lg:text-4xl">
                        {stat.value}
                      </p>
                    </div>
                    <div
                      className={`${stat.color} flex h-14 w-14 items-center justify-center rounded-2xl`}
                    >
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Metrics */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Quick Metrics - As of {currentDate}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {quickMetrics.map(metric => (
                <div key={metric.label} className="rounded-lg bg-muted p-4">
                  <p className="mb-1 text-sm text-muted-foreground">
                    {metric.label}
                  </p>
                  <p className="text-2xl font-bold text-primary">
                    {metric.count}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Chart */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Properties vs Months</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="properties"
                  stroke="hsl(var(--primary))"
                  strokeWidth={3}
                  dot={{ fill: "hsl(var(--primary))", r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Dashboard;
